import { contentDb, type PageDetailRow, type PageItem, type PageRow } from '@/db/content';
import { DEFAULT_LOCALE } from '@/i18n';
import { replaceDetailUrlPageId } from '@/navTree';

/** Collect all Wagtail page ids for this logical page (canonical + locale_variants). */
export function allPageIdsForListingItem(item: PageItem): number[] {
  const ids = new Set<number>([item.id]);
  const v = item.locale_variants;
  if (v) {
    for (const row of Object.values(v)) {
      if (row?.id != null) ids.add(row.id);
    }
  }
  return [...ids];
}

function localeForPageId(row: PageRow, pageId: number): string | null {
  if (row.canonical_page_id === pageId) return row.listingItem.meta.locale;
  const v = row.listingItem.locale_variants;
  if (!v) return null;
  for (const [loc, ref] of Object.entries(v)) {
    if (ref?.id === pageId) return loc;
  }
  return null;
}

function listingOnlyFieldsFromListingItem(item: PageItem): Partial<PageDetailRow> {
  const out: Partial<PageDetailRow> = {};
  if (item.date !== undefined) out.date = item.date;
  if (item.category !== undefined) out.category = item.category;
  if (item.tags !== undefined) out.tags = item.tags;
  if (item.image !== undefined) (out as Record<string, unknown>).image = item.image;
  if (item.rich_title !== undefined) (out as Record<string, unknown>).rich_title = item.rich_title;
  if (item.header_style !== undefined)
    (out as Record<string, unknown>).header_style = item.header_style;
  return out;
}

export function pageItemFromDetailSlice(slice: Record<string, unknown>): PageItem {
  const meta = { ...(slice.meta as object) } as PageItem['meta'];
  return {
    id: slice.id as number,
    title: typeof slice.title === 'string' ? slice.title : '',
    meta,
  };
}

/**
 * Build a single-locale detail row for IndexedDB hydration / store (matches former PageDetailRow).
 */
export function extractDetailSliceFromConsolidated(
  row: PageRow,
  pageId: number
): PageDetailRow | null {
  const loc = localeForPageId(row, pageId);
  if (!loc) return null;
  const fromBundle = row.locales?.[loc];
  if (fromBundle && typeof fromBundle === 'object') {
    const id = (fromBundle as { id?: number }).id ?? pageId;
    const detailDate = row.detailDates?.[loc] ?? null;
    return {
      ...(fromBundle as Record<string, unknown>),
      id,
      detailDate,
      detailClientGeneration: row.detailClientGeneration,
    } as PageDetailRow;
  }
  if (loc === row.listingItem.meta.locale && row.canonical_page_id === pageId) {
    const m = row.listingItem.meta;
    return {
      id: pageId,
      title: row.listingItem.title,
      meta: { ...m },
      ...listingOnlyFieldsFromListingItem(row.listingItem),
      detailClientGeneration: row.detailClientGeneration,
    } as PageDetailRow;
  }
  const v = row.listingItem.locale_variants?.[loc];
  if (!v) return null;
  return {
    id: v.id,
    title: v.title,
    meta: {
      type: row.listingItem.meta.type,
      detail_url: replaceDetailUrlPageId(row.listingItem.meta.detail_url, v.id),
      html_url: row.listingItem.meta.html_url ?? null,
      url_path: v.url_path,
      path: v.path,
      slug: v.slug,
      first_published_at: row.listingItem.meta.first_published_at ?? null,
      last_published_at: v.last_published_at,
      locale: loc,
      translation_key: row.listingItem.meta.translation_key,
      show_in_menus: row.listingItem.meta.show_in_menus,
      seo_title: row.listingItem.meta.seo_title ?? null,
      search_description: row.listingItem.meta.search_description ?? null,
      icon: row.listingItem.meta.icon ?? null,
    },
    detailClientGeneration: row.detailClientGeneration,
  } as PageDetailRow;
}

export async function findConsolidatedRowByPageId(
  pageId: number
): Promise<PageRow | undefined> {
  try {
    const rows = await contentDb.page.toArray();
    return rows.find((r) => r.all_page_ids.includes(pageId));
  } catch {
    return undefined;
  }
}

/** Latest If-Modified-Since header value from stored per-locale detail dates. */
export function maxDetailDateForRequest(row: PageRow | undefined): string | null {
  if (!row?.detailDates) return null;
  const dates = Object.values(row.detailDates).filter(
    (d): d is string => typeof d === 'string' && d.length > 0
  );
  if (!dates.length) return null;
  return dates.reduce((a, b) => (a > b ? a : b));
}

export function mergeApiBundleIntoRow(
  existing: PageRow | undefined,
  bundle: Record<string, unknown>
): PageRow {
  const translation_key = String(bundle.translation_key ?? '');
  const canonical_page_id = Number(bundle.canonical_page_id ?? 0);
  const localesPayload = bundle.locales as
    | Record<string, Record<string, unknown> | null>
    | undefined;

  const detailDates: NonNullable<PageRow['detailDates']> = { ...(existing?.detailDates ?? {}) };
  const locales: NonNullable<PageRow['locales']> = { ...(existing?.locales ?? {}) };
  if (localesPayload && typeof localesPayload === 'object') {
    for (const [loc, slice] of Object.entries(localesPayload)) {
      if (!slice || typeof slice !== 'object') {
        delete locales[loc];
        detailDates[loc] = null;
        continue;
      }
      locales[loc] = slice;
      const meta = slice.meta as { last_published_at?: string | null } | undefined;
      detailDates[loc] = meta?.last_published_at ?? null;
    }
  }

  let listingItem = existing?.listingItem;
  if (!listingItem) {
    const tetSlice =
      localesPayload?.[DEFAULT_LOCALE] ??
      localesPayload?.tet ??
      localesPayload?.['tet'];
    if (tetSlice && typeof tetSlice === 'object') {
      listingItem = pageItemFromDetailSlice(tetSlice as Record<string, unknown>);
    }
  }
  if (!listingItem) {
    throw new Error('mergeApiBundleIntoRow: missing listingItem and no Tetum slice in bundle');
  }
  const tk = translation_key || listingItem.meta.translation_key;
  if (!tk) {
    throw new Error('mergeApiBundleIntoRow: missing translation_key');
  }
  return {
    translation_key: tk,
    canonical_page_id: canonical_page_id || existing?.canonical_page_id || listingItem.id,
    all_page_ids: allPageIdsForListingItem(listingItem),
    listingItem,
    locales,
    detailDates,
    detailClientGeneration: existing?.detailClientGeneration,
  };
}

export function buildConsolidatedRowFromListingItem(
  item: PageItem,
  existing?: PageRow
): PageRow {
  const tk = item.meta.translation_key;
  if (!tk) {
    throw new Error('Listing item missing translation_key');
  }
  return {
    translation_key: tk,
    canonical_page_id: item.id,
    all_page_ids: allPageIdsForListingItem(item),
    listingItem: item,
    locales: existing?.locales,
    detailDates: existing?.detailDates,
    detailClientGeneration: existing?.detailClientGeneration,
  };
}
